import React, { useRef, useState } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend';
import { DndProvider, useDrop } from 'react-dnd';
import { useDidUpdate } from 'rooks';
import { Icon } from '@seeqdev/qomponents';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';

export interface FileDropperProps {
  text: string;
  fileDropCallback: (file: File) => Promise<void>;
  clearFileCallback: () => void;
  id: string;
  testId?: string;
  addFileIndicator?: React.ReactNode;
  actionText?: string;
  isValid?: boolean;
  filename?: string;
  fileExtension?: string;
  extraClassNames?: string;
  initUploadCount?: number;
}

const InnerFileDropper: React.FunctionComponent<FileDropperProps> = (props) => {
  const {
    text,
    fileDropCallback,
    clearFileCallback,
    extraClassNames,
    fileExtension,
    id,
    testId = 'fileDropper',
    filename,
    isValid = true,
    addFileIndicator,
    actionText,
    initUploadCount,
  } = props;
  const [hovering, setHovering] = useState(false);
  const [isProcessing, setProcessing] = useState(false);

  const { t } = useTranslation();

  const handleFileChange = (file: File) => {
    setProcessing(true);
    fileDropCallback(file).finally(() => setProcessing(false));
  };

  const [, drop] = useDrop({
    accept: [NativeTypes.FILE],
    hover(item: any) {
      if (!isProcessing) {
        setHovering(true);
      }
    },
    drop(item) {
      if (isProcessing) {
        return;
      }

      handleFileChange(item.files[0]);
      setHovering(false);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  const dropRef = useRef<HTMLDivElement>(null);
  drop(dropRef);

  const inputFileRef = useRef<HTMLInputElement>(null);

  useDidUpdate(() => {
    !filename && inputFileRef.current?.click();
  }, [initUploadCount]);

  const renderAddFileIndicatorWithText = () => (
    <div className="flexColumnContainer flexAlignCenter pl10 pr10">
      {addFileIndicator ? addFileIndicator : <span className="fa-3x">+</span>}
      <span className="ml10 text-left">{t(text)}</span>
    </div>
  );

  return (
    <div
      data-testid={testId}
      className={classNames(
        filename ? 'file-drop-zone-uploaded' : 'file-drop-zone',
        extraClassNames,
        {
          'file-drop-zone-over': hovering,
          'file-drop-zone-error': !filename && !isValid,
        },
        'p5',
      )}
      ref={dropRef}
      onClick={() => !filename && inputFileRef.current?.click()}>
      {!filename && (
        <input
          data-testid={`${testId}Input`}
          multiple={false}
          type="file"
          accept={fileExtension}
          style={{ display: 'none' }}
          ref={inputFileRef}
          onChange={(event) => (event.target?.files?.[0] ? handleFileChange(event.target.files[0]) : _.noop)}
        />
      )}
      <div
        className={classNames(
          'flexColumnContainer',
          'flexSpaceBetween',
          'flexAlignCenter',
          filename ? 'min-height-75' : 'min-height-60',
        )}>
        {isProcessing && (
          <div className="flexColumnContainer flexAlignCenter pl10">
            <IconWithSpinner spinning={true} extraClassNames="fa-lg" />
            <span className="ml10">{t('LOADING')}</span>
          </div>
        )}
        {filename && !isProcessing && (
          <>
            <div className="flexRowContainer flexAlignStart pl5">
              <span>{t(actionText ? actionText : 'FILE_IMPORTED_AS')}</span>
              <span className="aggressiveWordBreak">{filename}</span>
            </div>
            <Icon
              icon="fa-close"
              extraClassNames="cursorPointer pr5"
              testId={`${id}-remove`}
              onClick={clearFileCallback}
            />
          </>
        )}
        {!filename && !isProcessing && renderAddFileIndicatorWithText()}
      </div>
    </div>
  );
};

const FileDropperUnwrapped: React.FunctionComponent<FileDropperProps> = (props) => (
  <DndProvider backend={HTML5Backend} context={window}>
    <InnerFileDropper {...props} />
  </DndProvider>
);

/**
 * Drag-and-drop element to receive file upload
 */
export const FileDropper = React.memo(FileDropperUnwrapped, (prev, next) => _.isEqual(prev, next));
